import { GroupOutlined, LockOutlined } from '@mui/icons-material';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../Button/Button';
import { Dialog } from '../Dialog';
import { Select } from '../Select/Select';
import { Input } from '../Input';
import { Banner } from '../Banner';

type CopyReportDialogProps = {
	defaultTitle: string;
	defaultIsPublic: boolean;
	reportIsDirty: boolean;
	onClose: () => void;
	onConfirm: (args: { title: string; isPublic: boolean }) => Promise<void>;
};

export function CopyReportDialog({
	defaultTitle,
	defaultIsPublic,
	reportIsDirty,
	onClose,
	onConfirm,
}: CopyReportDialogProps) {
	const [title, setTitle] = useState(defaultTitle);
	const [access, setAccess] = useState(defaultIsPublic ? 'public' : 'private');
	const [submitting, setSubmitting] = useState(false);
	const titleInputRef = useRef<HTMLInputElement>(null);

	// Focus the title input when the dialog opens
	// Just setting autoFocus on the input doesn't work due to MUI's FocusTrap misbehaviour...
	useEffect(() => {
		const id = setTimeout(() => {
			titleInputRef.current?.focus();
			titleInputRef.current?.select();
		}, 0);
		return () => clearTimeout(id);
	}, []);

	const handleSubmit = async () => {
		const trimmed = title.trim();
		if (!trimmed || submitting) {
			return;
		}
		setSubmitting(true);
		try {
			await onConfirm({ title: trimmed, isPublic: access === 'public' });
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog
			open
			barebones
			onClose={onClose}
			text={(
				<div
					className={clsx(
						'box-border flex w-full max-w-[500px] cursor-default flex-col gap-8',
						'rounded-lg border border-borders-primary bg-bg-static-surface_default',
						'px-10 py-8 shadow-[0px_4px_7px_2px_rgba(25,25,25,0.05)]',
					)}
					role="dialog"
				>
					<div className="flex min-w-0 flex-col gap-6">
						<h2
							className="m-0 text-lg font-semibold leading-6 tracking-[0.18px] text-text-primary"
						>
							Make a copy
						</h2>
						<div className="flex min-w-0 flex-col gap-8">
							<Input
								ref={titleInputRef}
								type="text"
								value={title}
								onChange={(e) => { setTitle(e.target.value); }}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										e.preventDefault();
										handleSubmit();
									}
								}}
							/>

							<div className="flex min-w-0 flex-col gap-4">
								<p className="m-0 text-base font-medium leading-6 tracking-[0.16px] text-text-primary">
									Who can access
								</p>
								<div className="flex w-full min-w-0 items-start justify-between gap-4">
									<div className="w-fit shrink-0">
										<Select
											variant="borderless"
											value={access}
											onChange={setAccess}
											options={[
												// TODO: Left for future sharing rework
												// {
												// 	value: 'link',
												// 	label: 'Anyone with the link',
												// 	icon: <LinkOutlined className="text-fg-primary" />,
												// },
												{
													value: 'public',
													label: 'Public',
													icon: <GroupOutlined className="text-fg-primary" />,
												},
												{
													value: 'private',
													label: 'Only me',
													icon: <LockOutlined className="text-fg-primary" />,
												},
											]}
											shrinkToValue
										/>
									</div>
									{/* TODO: Left for future sharing rework
									<div className="w-[120px] shrink-0">
										<Select
											variant="borderless"
											value="edit"
											onChange={() => {}}
											fullWidth
											options={[{ value: 'edit', label: 'Can edit' }]}
											shrinkToValue
										/>
									</div>
									*/}
								</div>
							</div>

							{reportIsDirty && (
								<Banner
									title="Unsaved changes"
									message="There are unsaved changes in your report. These changes will be included in the copy."
									status="warning"
								/>
							)}
						</div>
					</div>
					<div className="flex w-full gap-4">
						<Button
							variant="outlined"
							className="min-w-0 flex-1"
							onClick={onClose}
							disabled={submitting}
						>
							Cancel
						</Button>
						<Button
							variant="filled"
							className="min-w-0 flex-1"
							onClick={() => { handleSubmit(); }}
							disabled={!title.trim() || submitting}
						>
							Make a copy
						</Button>
					</div>
				</div>
			)}
		/>
	);
}
